/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'
// Load Quill's snow stylesheet so rendered posts use the EXACT same rules as the
// editor (line-height, list markers/indent, heading sizes). Content is wrapped in
// `.ql-snow > .ql-editor` below so these scoped rules apply.
import 'react-quill/dist/quill.snow.css'
import colors from '@sportninja/common/constants/appColors'
import { font } from 'src/components/css'
import { sanitizeHtmlUrls } from 'src/utils/stringUtils'

/**
 * @interface RichTextRendererProps
 * @property {string} content - The HTML string to render
 * @property {number} [truncateLength] - The character count at which to truncate the content (default: 300)
 * @property {boolean} [showReadMore] - A flag to control the "Read More/Less" functionality (default: true)
 * @property {string} [readMoreAlign] - The alignment of the "Read More" button (default: center)
 */
interface RichTextRendererProps {
  content: string
  truncateLength?: number
  showReadMore?: boolean
  readMoreAlign?: 'center' | 'left' | 'right'
}

/**
 * RichTextRenderer component - renders sanitized HTML content with consistent styling
 *
 * This component provides a safe and consistent way to display rich HTML content
 * across the application. It includes:
 * - HTML sanitization to prevent XSS attacks
 * - Consistent styling for all rich text elements
 * - Optional content truncation with "Read More/Less" functionality
 * - Support for text alignment classes from Quill editor
 *
 * The component applies comprehensive styling for headers, paragraphs, lists,
 * links, blockquotes, and other rich text elements while maintaining security
 * through URL sanitization.
 *
 * @param props - Component props as defined in RichTextRendererProps
 * @returns JSX element representing the rendered rich text content
 */
const RichTextRenderer: React.FC<RichTextRendererProps> = ({
  content,
  truncateLength = 300,
  showReadMore = true,
  readMoreAlign = 'center',
}) => {
  const [isExpanded, setIsExpanded] = useState(false)

  // Don't show anything if content is empty
  if (!content?.trim()) {
    return null
  }

  const shouldTruncate = showReadMore && content.length > truncateLength
  const displayContent =
    shouldTruncate && !isExpanded
      ? content.substring(0, truncateLength) + '...'
      : content

  return (
    <div className='ql-snow'>
      {/*
        We render the SAME HTML the Quill editor produces, so we mirror Quill's
        snow content rules here directly instead of relying on the global
        stylesheet. The app loads a Meyer reset (`ol,ul { list-style:none; margin:0;
        padding:0 }`) which would otherwise strip list markers/indent and make the
        post look nothing like the editor. Every rule below is a verbatim port of
        `react-quill/dist/quill.snow.css` (the editor's stylesheet), scoped to
        `.rich-text-content` and given enough specificity (`&&`, plus `!important`
        only where the reset competes) to win deterministically regardless of CSS
        load order. Keep these in sync with the Quill version if it's upgraded.
      */}
      <div
        className='rich-text-content ql-editor'
        css={css`
          /* ---- base (snow .ql-editor) ---- */
          && {
            ${font.body}
            color: ${colors.NEUTRAL_0};
            line-height: 1.42;
            text-align: left;
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-wrap: break-word;
            /* snow's .ql-editor adds 12px 15px editor padding; this is display
               content, not a textbox, so strip it to sit flush with title/image */
            padding: 0;
          }

          /* snow: all block elements reset margin/padding, counters seeded on lists */
          p,
          ol,
          ul,
          pre,
          blockquote,
          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            margin: 0;
            padding: 0;
            counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7
              list-8 list-9;
          }

          /* Preserve author's blank lines: an empty Quill line is a block with only
             a <br> (e.g. <p><br></p> or <h3><br></h3>). In a static render (no
             contenteditable) such a block can collapse to zero height, so the
             spacing the author added in the editor vanishes. Force every text block
             to occupy at least one line so blank lines survive. */
          p,
          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            min-height: 1.42em;
          }

          /* snow heading sizes (.ql-snow .ql-editor hN) */
          h1 {
            font-size: 2em;
          }
          h2 {
            font-size: 1.5em;
          }
          h3 {
            font-size: 1.17em;
          }
          h4 {
            font-size: 1em;
          }
          h5 {
            font-size: 0.83em;
          }
          h6 {
            font-size: 0.67em;
          }

          /* ---- lists: override the Meyer reset (list-style:none, margin/padding:0) ---- */
          ol,
          ul {
            padding-left: 1.5em !important;
            margin: 0 !important;
            list-style: none !important;
          }
          ol > li,
          ul > li {
            list-style-type: none !important;
          }
          li {
            position: relative;
            padding-left: 1.5em !important;
          }
          li::before {
            display: inline-block;
            white-space: nowrap;
            width: 1.2em;
            margin-left: -1.5em;
            margin-right: 0.3em;
            text-align: right;
          }
          ul > li::before {
            content: '\\2022';
          }
          ol li {
            counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7
              list-8 list-9;
            counter-increment: list-0;
          }
          ol li::before {
            content: counter(list-0, decimal) '. ';
          }

          /* nested ordered-list indent levels (lower-alpha / lower-roman, matching snow) */
          ol li.ql-indent-1 {
            counter-increment: list-1;
          }
          ol li.ql-indent-1::before {
            content: counter(list-1, lower-alpha) '. ';
          }
          ol li.ql-indent-2 {
            counter-increment: list-2;
          }
          ol li.ql-indent-2::before {
            content: counter(list-2, lower-roman) '. ';
          }

          /* indent padding (snow .ql-indent-N) */
          .ql-indent-1:not(.ql-direction-rtl) {
            padding-left: 3em;
          }
          .ql-indent-2:not(.ql-direction-rtl) {
            padding-left: 6em;
          }
          .ql-indent-3:not(.ql-direction-rtl) {
            padding-left: 9em;
          }

          /* ---- inline marks ---- */
          strong {
            font-weight: bold;
          }
          em {
            font-style: italic;
          }
          u {
            text-decoration: underline;
          }
          s {
            text-decoration: line-through;
          }

          a {
            color: ${colors.PRIMARY};
            text-decoration: underline;
            &:hover {
              opacity: 0.8;
            }
          }

          img {
            max-width: 100%;
            height: auto;
            border-radius: 4px;
          }

          blockquote {
            border-left: 4px solid ${colors.PRIMARY};
            padding-left: 16px;
            margin: 16px 0 !important;
            opacity: 0.9;
          }

          /* ---- alignment (snow .ql-align-*) ---- */
          .ql-align-left {
            text-align: left;
          }
          .ql-align-center {
            text-align: center;
          }
          .ql-align-right {
            text-align: right;
          }
          .ql-align-justify {
            text-align: justify;
          }
        `}
        dangerouslySetInnerHTML={{ __html: sanitizeHtmlUrls(displayContent) }}
      />
      {shouldTruncate && (
        <button
          css={css`
            background: none;
            border: none;
            color: ${colors.PRIMARY};
            cursor: pointer;
            padding: 8px 0;
            font-size: 14px;
            text-decoration: underline;
            &:hover {
              opacity: 0.8;
            }
            width: 100%;
            text-align: ${readMoreAlign};
          `}
          onClick={(e) => {
            e.stopPropagation()
            setIsExpanded(!isExpanded)
          }}
        >
          {isExpanded ? 'Read Less' : 'Read More'}
        </button>
      )}
    </div>
  )
}

export default RichTextRenderer
